import { AlarmClock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/shared/ui/Badge";
import { useNowTick } from "@/widgets/control/useControlData";
import { ackTargetInfo, formatDuration } from "./alertText";
import type { Alert } from "@/shared/api/models";

/** 아직 [확인] 안 한 경보의 「확인 목표 1분 · 40초 남음 / 넘김」(2026-10-29, 10-12 검증 V-31).
 *  목표 안이면 회색, 넘기면 빨강 — 문구에 「넘김」이 있어 등급색과 헷갈리지 않는다.
 *  열린 경보에만 붙으므로 5초 시계도 그 행에만 돈다. */
export function AckTargetBadge({ alert }: { alert: Alert }) {
  const { t } = useTranslation();
  const now = useNowTick(5_000);
  const info = ackTargetInfo(alert, now);
  if (!info || info.acked) return null;
  const target = formatDuration(t, info.targetS);
  return (
    <Badge tone={info.over ? "danger" : "neutral"} title={t("widgets.alerts.v2.ackTarget.title")}>
      <AlarmClock aria-hidden size={12} strokeWidth={2} className="shrink-0" />
      {info.over
        ? t("widgets.alerts.v2.ackTarget.over", { target, over: formatDuration(t, info.elapsedS - info.targetS) })
        : t("widgets.alerts.v2.ackTarget.left", { target, left: formatDuration(t, info.targetS - info.elapsedS) })}
    </Badge>
  );
}

/** 상세 처리 흐름의 [확인] 칸 아래 한 줄 — 눌렀으면 걸린 시간, 아직이면 남은/넘긴 시간. */
export function AckTargetNote({ alert }: { alert: Alert }) {
  const { t } = useTranslation();
  const now = useNowTick(alert.state === "open" ? 5_000 : 600_000);
  const info = ackTargetInfo(alert, now);
  if (!info) return null;
  const target = formatDuration(t, info.targetS);
  const dur = formatDuration(t, info.acked ? info.elapsedS : Math.abs(info.targetS - info.elapsedS));
  const key = info.acked
    ? info.over ? "tookOver" : "took"
    : info.over ? "overShort" : "leftShort";
  return (
    <div
      className={
        "mt-0.5 text-[11px] tabular-nums break-keep " +
        (info.over ? "font-semibold text-[var(--danger-fg)]" : "text-[var(--fg-subtle)]")
      }
      title={t("widgets.alerts.v2.ackTarget.title")}
    >
      {t(`widgets.alerts.v2.ackTarget.${key}`, { target, dur })}
    </div>
  );
}
